/**
 * Selective Exam API Service
 *
 * Centralized API calls for the Selective Exam module.
 * All endpoints use the new modular backend API structure.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'https://mathcampus.vercel.app';

/**
 * Helper function to handle API responses
 */
const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
    throw new Error(error.detail || error.message || 'API request failed');
  }
  return response.json();
};

/**
 * Resolve the Supabase auth-token localStorage key.
 * Format: sb-{PROJECT_REF}-auth-token. Derived from REACT_APP_SUPABASE_URL,
 * falling back to scanning localStorage so it works in any environment.
 */
const getSupabaseAuthKey = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (supabaseUrl) {
    try {
      const projectRef = new URL(supabaseUrl).hostname.split('.')[0];
      return `sb-${projectRef}-auth-token`;
    } catch (e) {
      // fall through to localStorage scan
    }
  }
  return Object.keys(localStorage).find(k => /^sb-.+-auth-token$/.test(k)) || null;
};

/**
 * Helper function to get auth headers
 */
const getAuthHeaders = () => {
  let token = null;

  const authKey = getSupabaseAuthKey();
  const supabaseAuthData = authKey ? localStorage.getItem(authKey) : null;

  if (supabaseAuthData) {
    try {
      const authData = JSON.parse(supabaseAuthData);
      token = authData?.access_token;
    } catch (e) {
      console.error('[selectiveApi] Error parsing Supabase auth data:', e);
    }
  }

  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

/**
 * Selective Exam API
 */
export const selectiveApi = {

  // =============================================================================
  // SESSIONS API
  // =============================================================================
  sessions: {
    /**
     * Start a new practice or mock exam session
     * @param {Object} data - Session configuration
     * @returns {Promise<Object>} Session with first batch of questions
     */
    start: async (data) => {
      const response = await fetch(`${API_BASE}/api/selective/sessions/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    /**
     * Submit an answer for a question
     * @param {string} sessionId - Session UUID
     * @param {Object} data - Answer submission data
     * @returns {Promise<Object>} Answer feedback and next question info
     */
    submitAnswer: async (sessionId, data) => {
      const response = await fetch(`${API_BASE}/api/selective/sessions/${sessionId}/submit-answer`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    /**
     * Complete a session
     * @param {string} sessionId - Session UUID
     * @returns {Promise<Object>} Final session results
     */
    complete: async (sessionId) => {
      const response = await fetch(`${API_BASE}/api/selective/sessions/${sessionId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get session results
     * @param {string} sessionId - Session UUID
     * @returns {Promise<Object>} Detailed session results
     */
    getResults: async (sessionId) => {
      const response = await fetch(`${API_BASE}/api/selective/sessions/${sessionId}/results`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get current session state
     * @param {string} sessionId - Session UUID
     * @returns {Promise<Object>} Session state
     */
    getState: async (sessionId) => {
      const response = await fetch(`${API_BASE}/api/selective/sessions/${sessionId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * List user's sessions
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of sessions
     */
    list: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/sessions?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },
  },

  // =============================================================================
  // PROGRESS API
  // =============================================================================
  progress: {
    /**
     * Get user's progress matrix
     * @param {number} yearLevel - Year level (4, 5, or 6)
     * @returns {Promise<Object>} 336-cell progress matrix
     */
    getMatrix: async (yearLevel = 6) => {
      const response = await fetch(`${API_BASE}/api/selective/progress/matrix?year_level=${yearLevel}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get progress for specific domain
     * @param {string} domainCode - Domain code (e.g., "MATH-FR")
     * @returns {Promise<Object>} Domain-specific progress
     */
    getDomain: async (domainCode) => {
      const response = await fetch(`${API_BASE}/api/selective/progress/domain/${domainCode}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Unlock a tier for a domain
     * @param {string} domainCode - Domain code
     * @param {string} tier - Tier to unlock
     * @returns {Promise<Object>} Unlock confirmation
     */
    unlockTier: async (domainCode, tier) => {
      const response = await fetch(`${API_BASE}/api/selective/progress/unlock/${domainCode}/${tier}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get personalized recommendations
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of recommendations
     */
    getRecommendations: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/progress/recommendations?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get suggested practice session
     * @returns {Promise<Object>} Suggested session configuration
     */
    getSuggestedSession: async () => {
      const response = await fetch(`${API_BASE}/api/selective/progress/suggest-session`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get performance trends
     * @param {number} days - Number of days to analyze
     * @returns {Promise<Object>} Trend analysis
     */
    getTrends: async (days = 30) => {
      const response = await fetch(`${API_BASE}/api/selective/progress/trends?days=${days}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },
  },

  // =============================================================================
  // WRITING API
  // =============================================================================
  writing: {
    /**
     * Submit essay for AI assessment
     * @param {Object} data - Essay and prompt data
     * @returns {Promise<Object>} AI assessment results
     */
    assess: async (data) => {
      const response = await fetch(`${API_BASE}/api/selective/writing/assess`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    /**
     * Get assessment details
     * @param {string} assessmentId - Assessment UUID
     * @returns {Promise<Object>} Full assessment details
     */
    getAssessment: async (assessmentId) => {
      const response = await fetch(`${API_BASE}/api/selective/writing/assessments/${assessmentId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * List user's assessments
     * @param {number} limit - Maximum assessments to return
     * @returns {Promise<Array>} List of assessments
     */
    listAssessments: async (limit = 10) => {
      const response = await fetch(`${API_BASE}/api/selective/writing/assessments?limit=${limit}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get writing prompts
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} List of prompts
     */
    getPrompts: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/writing/prompts?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get specific prompt
     * @param {string} promptId - Prompt UUID
     * @returns {Promise<Object>} Prompt details
     */
    getPrompt: async (promptId) => {
      const response = await fetch(`${API_BASE}/api/selective/writing/prompts/${promptId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get assessment rubric
     * @param {string} writingType - Type of writing
     * @returns {Promise<Object>} Rubric details
     */
    getRubric: async (writingType = 'persuasive') => {
      const response = await fetch(`${API_BASE}/api/selective/writing/rubric?writing_type=${writingType}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },
  },

  // =============================================================================
  // CONTENT API
  // =============================================================================
  content: {
    /**
     * Get lessons
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of lessons
     */
    getLessons: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/content/lessons?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get specific lesson
     * @param {string} lessonId - Lesson UUID
     * @returns {Promise<Object>} Lesson details
     */
    getLesson: async (lessonId) => {
      const response = await fetch(`${API_BASE}/api/selective/content/lessons/${lessonId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get worked examples
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of examples
     */
    getExamples: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/content/examples?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get specific example
     * @param {string} exampleId - Example UUID
     * @returns {Promise<Object>} Example details
     */
    getExample: async (exampleId) => {
      const response = await fetch(`${API_BASE}/api/selective/content/examples/${exampleId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get strategies
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of strategies
     */
    getStrategies: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/content/strategies?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get glossary terms
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of glossary terms
     */
    getGlossary: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/content/glossary?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get all content for domain and tier
     * @param {string} domainCode - Domain code
     * @param {string} tier - Tier
     * @returns {Promise<Object>} All content (lessons, examples, strategies)
     */
    getAll: async (domainCode, tier) => {
      const response = await fetch(`${API_BASE}/api/selective/content/all/${domainCode}/${tier}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },
  },

  // =============================================================================
  // QUESTIONS API
  // =============================================================================
  questions: {
    /**
     * Search/filter questions
     * @param {Object} params - Query parameters
     * @returns {Promise<Array>} List of questions
     */
    search: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/questions?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get specific question
     * @param {string} questionId - Question UUID
     * @returns {Promise<Object>} Question details
     */
    get: async (questionId) => {
      const response = await fetch(`${API_BASE}/api/selective/questions/${questionId}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get question bank statistics
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Statistics
     */
    getStats: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/questions/stats/summary?${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get list of exam types (OC Exam, Selective Exam, etc.)
     * @returns {Promise<Array>} List of exam types
     */
    getExamTypes: async () => {
      const response = await fetch(`${API_BASE}/api/selective/meta/exam-types`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get list of sections
     * @param {string} examTypeId - Optional exam type filter
     * @returns {Promise<Array>} List of sections
     */
    getSections: async (examTypeId = null) => {
      const queryString = examTypeId ? `?exam_type_id=${examTypeId}` : '';
      const response = await fetch(`${API_BASE}/api/selective/meta/sections${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get list of domains
     * @param {string} sectionCode - Optional section filter
     * @param {string} examTypeId - Optional exam type filter
     * @returns {Promise<Array>} List of domains
     */
    getDomains: async (sectionCode = null, examTypeId = null) => {
      const params = new URLSearchParams();
      if (sectionCode) params.append('section_code', sectionCode);
      if (examTypeId) params.append('exam_type_id', examTypeId);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await fetch(`${API_BASE}/api/selective/meta/domains${queryString}`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },
  },

  // =============================================================================
  // ADMIN API (Admin only)
  // =============================================================================
  admin: {
    /**
     * Get admin statistics
     * @returns {Promise<Object>} Admin stats
     */
    getStats: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/stats`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get content statistics
     * @returns {Promise<Object>} Content stats
     */
    getContentStats: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/content-stats`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Check system status
     * @returns {Promise<Object>} System status
     */
    checkSystem: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/system-check`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Initialize system
     * @returns {Promise<Object>} Initialization result
     */
    initialize: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/initialize`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Update question
     * @param {string} questionId - Question UUID
     * @param {Object} data - Updated question data
     * @returns {Promise<Object>} Updated question
     */
    updateQuestion: async (questionId, data) => {
      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${questionId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handleResponse(response);
    },

    /**
     * Delete question
     * @param {string} questionId - Question UUID
     * @returns {Promise<Object>} Deletion confirmation
     */
    deleteQuestion: async (questionId) => {
      const response = await fetch(`${API_BASE}/api/selective/admin/questions/${questionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Bulk generate questions
     * @param {Object} params - Generation parameters
     * @returns {Promise<Object>} Generation result
     */
    bulkGenerate: async (params) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/admin/bulk-generate?${queryString}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Regenerate questions
     * @param {Object} params - Regeneration parameters
     * @returns {Promise<Object>} Regeneration result
     */
    regenerate: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE}/api/selective/admin/regenerate?${queryString}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Cleanup sample data
     * @returns {Promise<Object>} Cleanup result
     */
    cleanup: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/cleanup`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Get AI configuration settings
     * @returns {Promise<Object>} AI settings
     */
    getAISettings: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/ai-settings`, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response);
    },

    /**
     * Update AI configuration settings
     * @param {Object} settings - AI settings to update
     * @returns {Promise<Object>} Updated settings
     */
    updateAISettings: async (settings) => {
      const response = await fetch(`${API_BASE}/api/selective/admin/ai-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      });
      return handleResponse(response);
    },

    /**
     * Get AI provider status
     * @returns {Promise<Object>} Provider availability and status
     */
    getAIProviderStatus: async () => {
      const response = await fetch(`${API_BASE}/api/selective/admin/ai-providers/status`, {
        headers: getAuthHeaders(),
      });
      const data = await handleResponse(response);
      // Backend returns {success, providers, available_count, message}
      // Frontend expects just the providers object
      return data.providers || data;
    },
  },
};

export default selectiveApi;
